import React, { useMemo, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import JoditEditor from 'jodit-react';
import { FaBorderNone } from "react-icons/fa";
import { ENV } from '../../config/config';
import 'jodit/es5/jodit.min.css';
import './JoditEditor.css';

const JoditEditorComponent = ({ value, onChange, disabled = false, placeholder = '' }) => {
    const editor = useRef(null);

    const config = useMemo(() => ({
        readonly: disabled,
        placeholder: placeholder || 'Start typing...',
        height: 400,
        uploader: {
            url: `${ENV.url}ck-editor/upload`,
            format: 'json',
            filesVariableName: () => "editor", // Configures the form field name to Match backend expectation 'editor'
            headers: {
                Authorization: ENV.Authorization(),
                "x-auth-token": ENV.x_auth_token,
                "x-access-token": localStorage.getItem("accessToken"),
                "user-platform": 2,
            },
            prepareData: (data) => {
                // Determine if 'editor' key is needed. Jodit sends files in 'files[]' by default usually, 
                // but CKEditor endpoint expects 'editor'
                // Jodit allows renaming the field name via options.
                return data;
            },
            format: 'json',
            process: (resp) => {
                // Backend returns: { imageData: { filename: "..." } }
                // Jodit needs: { files: ["url"] }
                return {
                    files: [
                        `${ENV.ckeditorPath}${resp.imageData.filename}`
                    ]
                };
            },
            defaultHandlerSuccess: function (data, resp) {
                // Custom handler if needed, but 'process' usually handles the mapping
                // For Jodit v3/v4 standard behavior:
                if (data.files && data.files.length) {
                   this.selection.insertImage(data.files[0]);
                }
            },
            error: function (e) {
                console.error("Image upload error", e);
            }
        },
        filebrowser: {
             // If we had a file browser API
        },
        extraButtons: [
            {
                name: 'tableborder',
                icon: renderToStaticMarkup(<FaBorderNone />),
                tooltip: 'Table Border',
                list: {
                    '0': 'hidden',
                    '1': '1px',
                    '2': '2px',
                    '3': '3px'
                },
                exec: (...args) => {
                    const editor = args[0];
                    const current = args[1]; // The current node (e.g. <br> or <td>)
                    const controlWrapper = args[2]; // This contains { control: { name: '0', ... }, ... }
                    
                    // Valid options
                    const validOptions = ['0', '1', '2', '3'];

                    // Try to find the selected value
                    let val = null;

                    // 1. Check controlWrapper.control.name (The standard Jodit 3/4 way for lists)
                    if (controlWrapper && controlWrapper.control && validOptions.includes(controlWrapper.control.name)) {
                         val = controlWrapper.control.name;
                    }
                    // 2. Check controlWrapper.args (Alternate path)
                    else if (controlWrapper && controlWrapper.control && controlWrapper.control.args && validOptions.includes(controlWrapper.control.args[0])) {
                         val = controlWrapper.control.args[0];
                    }
                    // 3. Fallback: check direct name property if wrapper is the control itself
                    else if (controlWrapper && validOptions.includes(controlWrapper.name)) {
                        val = controlWrapper.name;
                    }

                    if (!val) {
                         return;
                    }

                    // Find table
                    let table = null;
                    const node = (current && current.nodeType) ? current : editor.selection.current(false);
                    
                    if (node) {
                        // Traverse up to find table
                        if (node.tagName === 'TABLE') {
                            table = node;
                        } else {
                            table = node.closest ? node.closest('table') : null;
                        }
                    }

                    if (!table) {
                         // Fallback to strict selection if 'current' was misleading
                         const sel = editor.selection.current(false);
                         if (sel) {
                             table = sel.closest ? sel.closest('table') : null;
                         }
                    }

                    if (table) {
                        if (String(val) === '0') {
                            table.setAttribute('border', '0');
                            table.style.border = 'none';
                             // Clear cell borders so they don't look weird
                             Array.from(table.querySelectorAll('td, th')).forEach(cell => {
                                cell.style.border = 'none';
                            });
                        } else {
                            table.setAttribute('border', String(val));
                            const borderStyle = `${val}px solid black`;
                            table.style.border = borderStyle;
                             Array.from(table.querySelectorAll('td, th')).forEach(cell => {
                                cell.style.border = borderStyle;
                            });
                        }
                        
                        editor.events.fire('change');
                    }
                }
            }
        ],
        buttons: [
            'source', '|',
            'bold', 'strikethrough', 'underline', 'italic', '|',
            'ul', 'ol', '|',
            'outdent', 'indent',  '|',
            'font', 'fontsize', 'brush', 'paragraph', '|',
            'image', 'video', 'table', 'link', '|',
            'align', 'undo', 'redo', '|',
            'hr', 'eraser', 'copyformat', '|',
            'fullsize', 'selectall', 'print',
            '|', 
        ],
        buttonsMD: [
            'source', '|',
            'bold', 'strikethrough', 'underline', 'italic', '|',
            'ul', 'ol', '|',
            'outdent', 'indent',  '|',
            'font', 'fontsize', 'brush', 'paragraph', '|',
            'image', 'video', 'table', 'link', '|',
            'align', 'undo', 'redo', '|',
            'hr', 'eraser', 'copyformat', '|',
            'fullsize', 'selectall', 'print',
            '|', 
        ],
        buttonsSM: [
            'source', '|',
            'bold', 'strikethrough', 'underline', 'italic', '|',
            'ul', 'ol', '|',
            'outdent', 'indent',  '|',
            'font', 'fontsize', 'brush', 'paragraph', '|',
            'image', 'video', 'table', 'link', '|',
            'align', 'undo', 'redo', '|',
            'hr', 'eraser', 'copyformat', '|',
            'fullsize', 'selectall', 'print',
            '|', 
        ],
        buttonsXS: [
            'source', '|',
            'bold', 'strikethrough', 'underline', 'italic', '|',
            'ul', 'ol', '|',
            'outdent', 'indent',  '|',
            'font', 'fontsize', 'brush', 'paragraph', '|',
            'image', 'video', 'table', 'link', '|',
            'align', 'undo', 'redo', '|',
            'hr', 'eraser', 'copyformat', '|',
            'fullsize', 'selectall', 'print',
            '|', 
        ],
        removeButtons: ['about', 'file', 'speech_recognize'] // Explicitly remove unwanted buttons
    }), [disabled, placeholder]);

    return (
        <JoditEditor
            ref={editor}
            value={value}
            config={config}
            tabIndex={1} // tabIndex of textarea
            onBlur={newContent => onChange(newContent)} // preferred to use onBlur for performance
            // onChange={newContent => {}}
        />
    );
};

export default JoditEditorComponent;